import { useState, useEffect } from 'react';
import '../../App.css';
import './user-control.css';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../app/hooks';

// images
import padlockIcon from '../../icons/padlock.512.png';

// types
import { NotificationType } from '../../utils/types';

// classes
import RoundButton from '../../tools/round-button';

import UserControlDropdownMenu from './user-control-dropdown-menu';

// types relating to events.
import { Event, EventType, EventTarget } from '../../utils/events';
import LanguageMenu from '../ui/language-selector/language-menu';

//	--------------------------------------------------------------------------
//
//	P R O P E R T I E S
//
//	--------------------------------------------------------------------------

//	--------------------------------------------------------------------------
//
//	H T M L   C U S T O M   C O M P O N E N T S
//
//	--------------------------------------------------------------------------

//	------------------------------------------------------------
//
//	Displays the login button with a different name depending
//	upon whether we're logged in or not.
//
//	------------------------------------------------------------

function UserAccountButton(args: {
  buttonHandler: any;
  initials: string;
  userDropdownMenuDisplayed: boolean;
  numNotifications: number;
}) {
  //
  //	--------------------------------------------------------------------------
  //
  //	R E D U X   S T A T E   H A N D L I N G
  //
  //	--------------------------------------------------------------------------

  // global state variables.
  const sG_LoggedIn: boolean | undefined = useAppSelector(
    (state) => state.global.loggedIn,
  );
  const sG_Username: string | undefined = useAppSelector(
    (state) => state.global.username,
  );

  //	--------------------------------------------------------------------------
  //
  //	D E C L A R A T I O N S
  //
  //	--------------------------------------------------------------------------

  // translation function
  const { t } = useTranslation();

  if (sG_LoggedIn === undefined)
    return (
      <RoundButton name="btnLogin" text={t('Please wait') + '...'} icon={padlockIcon} />
    );
  else if (sG_LoggedIn === false)
    return (
      <RoundButton
        name="btnLogin"
        text={t('Log in')}
        icon={padlockIcon}
        onClickHandler={args.buttonHandler}
      />
    );
  else
    return (
      <RoundButton
        name="btnUserAccount"
        text={sG_Username}
        abbreviatedText={args.initials}
        count={args.numNotifications}
        countOffset={true}
        onClickHandler={args.buttonHandler}
      />
    );
} // UserAccountButton

//	--------------------------------------------------------------------------
//
//	C O M P O N E N T   D E F I N I T I O N
//
//	--------------------------------------------------------------------------

export default function UserControl(props: {
  buttonHandler: any;
  userDropdownMenuDisplayed: boolean;
  setUserDropdownMenuDisplayed: any;
}) {
  //	--------------------------------------------------------------------------
  //
  //	R E D U X   S T A T E   H A N D L I N G
  //
  //	--------------------------------------------------------------------------
  // global state variables.
  const sG_Username: string | undefined = useAppSelector(
    (state) => state.global.username,
  );

  // app state variables.
  const sNotifications: NotificationType[] = useAppSelector(
    (state) => state.app.notifications,
  );

  //	--------------------------------------------------------------------------
  //
  //	D E C L A R A T I O N S
  //
  //	--------------------------------------------------------------------------

  // translation function
  const { t } = useTranslation();

  // loop over all the characters of the username to extract the initials.
  let initials: string = '';
  let spaceLast: boolean = true;
  if (sG_Username !== undefined)
    for (let index = 0; index < sG_Username.length; index++) {
      if (spaceLast === true && initials.length < 3)
        initials += sG_Username.slice(index, index + 1);
      spaceLast = sG_Username.slice(index, index + 1) === ' ';
    }

  const [sInitials, setInitials] = useState<string>(initials);

  //	--------------------------------------------------------------------------
  //
  //	E V E N T   D E C L A R A T I O N S
  //
  //	--------------------------------------------------------------------------

  const [sEventToDropdownMenu, setEventToDropdownMenu] = useState<any>(undefined);

  const eventToDropdownMenu = (newFunction: any) => {
    setEventToDropdownMenu(() => newFunction);
  }; // eventToDropdownMenu

  //	--------------------------------------------------------------------------
  //
  //	F U N C T I O N S
  //
  //	--------------------------------------------------------------------------

  //	------------------------------------------------------------
  //
  //	handle event coming from the parent component.
  //
  //	------------------------------------------------------------

  function eventHandler(args: { event: Event }) {
    console.log('user-control event handler fires', EventType[args.event.eventType]);

    // if the incoming event is global, or destined for a child component, then
    // pass it on.
    if (
      args.event.eventTarget === EventTarget.GLOBAL &&
      sEventToDropdownMenu !== undefined
    )
      sEventToDropdownMenu({ event: args.event });
  } // eventHandler

  //	-------------------------------------------------
  //
  //	count the number of notifications that are
  //	unread.
  //
  //	-------------------------------------------------

  function numUnreadNotifications(args: { notifications: NotificationType[] }) {
    var foundUnread: number = 0;
    for (var i: number = 0; i < args.notifications.length; i++)
      if (args.notifications[i].readFlag === false) foundUnread = foundUnread + 1;

    // return something.
    return foundUnread;
  } // numUnreadNotifications

  //	------------------------------------------------------------
  //
  //	Component code
  //
  //	------------------------------------------------------------

  return (
    <div>
      <div
        className="user-control"
        data-dropdown-displayed={props.userDropdownMenuDisplayed === true ? 'T' : 'F'}
        data-contains-notifications={sNotifications.length > 0 ? 'T' : 'F'}
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'right',
          padding: '10px 10px 0px 0px',
          borderBottom: 'none',
        }}
      >
        {UserAccountButton({
          buttonHandler: props.buttonHandler,
          initials: sInitials,
          userDropdownMenuDisplayed: props.userDropdownMenuDisplayed,
          numNotifications: numUnreadNotifications({
            notifications: sNotifications,
          }),
        })}
        <UserControlDropdownMenu
          sUserDropdownMenuDisplayed={props.userDropdownMenuDisplayed}
          setUserDropdownMenuDisplayed={props.setUserDropdownMenuDisplayed}
          buttonHandler={props.buttonHandler}
        />
      </div>
      <div style={{ margin: '5px 0px 0px 0px' }}>
        <LanguageMenu />
      </div>
    </div>
  );
} // UserControl
